import { Config } from 'config/envConfig';
import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFile,
    faFolder,
} from '@fortawesome/free-solid-svg-icons';
import 'react-reflex/styles.css';
import { ReflexContainer, ReflexElement } from 'react-reflex';
import { ChatActions } from './chatActions';
import { ChatDisplay } from './chatDisplay';
import {
    getFileIcon,
    getFileIconColor,
    getRepoIcon,
    selectionModeCode,
} from '@quyva/common';
import { Button } from '@quyva/shared-ui';
import axios from 'axios';
import { getUrl } from '../pdfDisplay/utils';
import { ResponseEnum } from '../../enums';

export const ChatContainer = ({
    activeQuestion,
    questionsData,
    isGenerating,
    flagMode,
    query,
    currentQnA,
    fineTunedMode,
    repoName,
    user,
    setFlagMode,
    setFlagIndex,
    setQuery,
    handleChartFormat,
    handleTableFormat,
    handleListFormat,
    // handleCustomFormat,
    handleFineTune,
    handleSubmit,
    handleRegenerate,
    setFineTuningVerify,
    fileToChat,
    isDatabase,
    togglePdfVisibility,
    repository,
    processingRepos,
    repoId,
    uploadedFiles,
    setUploadedFiles,
    attachmentsList,
    setAttachmentsList,
    qnaLoadingText,
    handleGetQnAHistory,
    chatForm,
    formPending,
    onFormSubmit,
    onFormCancel,
}) => {
    const BREAKPOINT_WIDTH = 1024;
    const [pendingQuickSubmit, setPendingQuickSubmit] = useState(false);
    const [activeAnswer, setActiveAnswer] = useState('');
    const [virtualRepos, setVirtualRepos] = useState([]);
    const [selectedVirtualRepoId, setSelectedVirtualRepoId] = useState(null);
    const [hasDefaultVirtualRepo, setHasDefaultVirtualRepo] = useState(false);

    // repository can be absent depending on which pane renders this container,
    // so fall back to repoName - the folder name is only informational for
    // /get-qna-history/ (it filters on repoid), and bailing out here would leave
    // the previous repo's/virtual repo's history on screen.
    const folderNameForHistory = repository?.name || repoName;

    const fetchHistoryForRepoId = async (repoid) => {
        try {
            if (!handleGetQnAHistory) return;
            if (!user?.email) return;
            if (!folderNameForHistory) return;
            if (!repoid) return;
            // Keep the file scope too, so switching virtual repos with a file
            // selected loads that file's history for the newly selected repo.
            handleGetQnAHistory(
                user.email,
                folderNameForHistory,
                repoid,
                false,
                fileToChat || null
            );
        } catch (e) {
            return;
        }
    };

    const fetchVirtualRepos = async () => {
        if (!repoId) {
            setVirtualRepos([]);
            setSelectedVirtualRepoId(null);
            setHasDefaultVirtualRepo(false);
            return;
        }

        try {
            const url = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/virtual_repo_routes/virtual-repos`
            );

            const currentUser = JSON.parse(
                localStorage.getItem('current') || '{}'
            );

            const response = await axios.get(url, {
                params: {
                    parent_repo_id: repoId,
                },
            });

            const data = response?.data || {};
            console.log('response: ', response.data);
            const list = data.virtual_repos || [];
            setVirtualRepos(list);

            const defaultId = data.default_virtual_repo_id ?? null;
            setSelectedVirtualRepoId(defaultId);
            // Show dropdown whenever there is at least one virtual repo
            setHasDefaultVirtualRepo(Array.isArray(list) && list.length > 0);

            const repoidToFetch = defaultId === null || defaultId === undefined ? repoId : defaultId;
            fetchHistoryForRepoId(repoidToFetch);
        } catch (err) {
            // console.log('virtual-repos api called.');
            setVirtualRepos([]);
            setSelectedVirtualRepoId(null);
            setHasDefaultVirtualRepo(false);
            fetchHistoryForRepoId(repoId);
        }
    };

    // Fetch virtual repositories for the current repo
    useEffect(() => {
        fetchVirtualRepos();
    }, [repoId]);

    // Refetch history when the selected file changes
    const prevFileToChatRef = useRef(fileToChat);
    useEffect(() => {
        const prev = prevFileToChatRef.current;
        prevFileToChatRef.current = fileToChat;
        // Skip on initial render (repoId effect already loaded history)
        if (prev === fileToChat) return;
        if (!repoId) return;
        // File selected/deselected — reload history (file-scoped or repo-level)
        // for whichever (virtual) repo is currently selected
        fetchHistoryForRepoId(selectedVirtualRepoId ?? repoId);
    }, [fileToChat]);

    const handleVirtualRepoSelection = (virtualRepoId) => {
        const repoidToFetch =
            virtualRepoId === null || virtualRepoId === undefined ? repoId : virtualRepoId;
        fetchHistoryForRepoId(repoidToFetch);
    };

    const selectedVirtualRepoName =
        selectedVirtualRepoId === null || selectedVirtualRepoId === undefined
            ? null
            : virtualRepos.find((v) => v.virtual_repo_id === selectedVirtualRepoId)
                  ?.virtual_repo_name || null;

    const displayRepoName = selectedVirtualRepoName
        ? `${repoName} (${selectedVirtualRepoName})`
        : repoName;

    // Refresh virtual repo dropdown in real time when Configure Repository saves
    useEffect(() => {
        const handler = (e) => {
            const updatedRepoId = e?.detail?.parent_repo_id ?? null;
            if (!updatedRepoId) return;
            if (String(updatedRepoId) !== String(repoId)) return;
            fetchVirtualRepos();
        };

        window.addEventListener('virtual-repos-updated', handler);
        return () => window.removeEventListener('virtual-repos-updated', handler);
    }, [repoId]);

    useEffect(() => {
        if (
            activeQuestion &&
            activeQuestion.answer &&
            activeAnswer !== activeQuestion.answer
        ) {
            setActiveAnswer(activeQuestion.answer);
        }
    }, [activeQuestion]);

    const handleQuickSubmit = (actualQuery) => {
        const selectionModeQuery = `${selectionModeCode} ${actualQuery}`;
        setQuery(selectionModeQuery);
        setPendingQuickSubmit(true); // tell useEffect to run handleSubmit after query is set
    };
    useEffect(() => {
        if (pendingQuickSubmit && query) {
            handleSubmit(false);
            setPendingQuickSubmit(false);
            setQuery('');
        }
    }, [pendingQuickSubmit, query]);

    return (
        <ReflexContainer orientation='horizontal'>
            {window.innerWidth <= BREAKPOINT_WIDTH &&
                !isDatabase &&
                activeQuestion && (
                    <div className='fixed bottom-24 right-8 z-20 scale-110'>
                        <Button
                            className={'mx-2 w-30 h-30'}
                            //text={'View Source'}
                            hoverText={'View Source'}
                            icon={faFile}
                            onClick={(e) => {
                                togglePdfVisibility();
                            }}
                            type={'success'}
                        />
                    </div>
                )}
            <ReflexElement
                className='header border-b shadow-sm'
                style={{
                    minHeight: '3.5rem',
                    height: '3.5rem',
                    maxHeight: '3.5rem',
                }}
            >
                <div className='pane-content w-full h-full p-2 select-none'>
                    <div className='flex h-full items-center my-0 mx-2'>
                        <div className='w-1/2 my-1 text-texts-secondary300 truncate'>
                            <div className='flex items-center'>
                                {fileToChat === undefined ||
                                fileToChat === '' ? (
                                    <FontAwesomeIcon
                                        icon={getRepoIcon(repository)}
                                        //color='gold'
                                        className='mr-2 drop-shadow-sm text-backgrounds-primary200'
                                    />
                                ) : (
                                    <FontAwesomeIcon
                                        icon={getFileIcon(fileToChat)}
                                        color={getFileIconColor(fileToChat)}
                                        className='mr-2 drop-shadow-sm'
                                    />
                                )}{' '}
                                {fileToChat === undefined ||
                                fileToChat === '' ? (
                                    <div className='truncate font-semibold text-base'>
                                        {displayRepoName}
                                    </div>
                                ) : (
                                    <div className='flex items-center truncate font-semibold w-full'>
                                        <div className='truncate text-base mr-2'>
                                            {fileToChat}
                                        </div>
                                        <div className='truncate text-sm'>
                                            ({displayRepoName})
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        {
                            //if needed add button here
                        }
                        <div className='w-1/2 flex justify-end'>
                        </div>
                    </div>
                </div>
            </ReflexElement>

            <ReflexElement flex={1} className='body'>
                <ChatDisplay
                    activeQuestion={activeQuestion}
                    questionsData={questionsData}
                    flagMode={flagMode}
                    repoName={displayRepoName}
                    setQuery={setQuery}
                    user={user}
                    setFlagMode={setFlagMode}
                    setFlagIndex={setFlagIndex}
                    handleChartFormat={handleChartFormat}
                    handleTableFormat={handleTableFormat}
                    handleListFormat={handleListFormat}
                    isGenerating={isGenerating}
                    fileToChat={fileToChat}
                    isDatabase={isDatabase}
                    repository={repository}
                    setAttachmentsList={setAttachmentsList}
                    loadingText={qnaLoadingText}
                    chatForm={chatForm}
                    onFormSubmit={onFormSubmit}
                    onFormCancel={onFormCancel}
                />
            </ReflexElement>

            <ReflexElement
                className='footer border-t shadow'
                style={{
                    minHeight: '6rem',
                    height: '6rem',
                    maxHeight: '6rem',
                }}
            >
                <div className='pane-content w-full h-full'>
                    <div className='flex items-center justify-end w-full h-full'>
                        <ChatActions
                            query={query}
                            fineTunedMode={fineTunedMode}
                            repoName={displayRepoName}
                            currentQnA={currentQnA}
                            setQuery={setQuery}
                            handleFineTune={handleFineTune}
                            handleSubmit={handleSubmit}
                            handleRegenerate={handleRegenerate}
                            isGenerating={isGenerating}
                            formPending={formPending}
                            fileToChat={fileToChat}
                            isDatabase={isDatabase}
                            processingRepos={processingRepos}
                            repoId={repoId}
                            attachmentsList={attachmentsList}
                            setAttachmentsList={setAttachmentsList}
                            uploadedFiles={uploadedFiles}
                            setUploadedFiles={setUploadedFiles}
                            repository={repository}
                            hasDefaultVirtualRepo={hasDefaultVirtualRepo}
                            virtualRepos={virtualRepos}
                            selectedVirtualRepoId={selectedVirtualRepoId}
                            setSelectedVirtualRepoId={setSelectedVirtualRepoId}
                            onVirtualRepoSelected={handleVirtualRepoSelection}
                        />
                    </div>
                </div>
            </ReflexElement>
        </ReflexContainer>
    );
};
